import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Banknote, Upload, RefreshCw, FileText, Mail, GraduationCap, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { courseRequiresPecb, PECB_SIGNUP_URL } from "../lib/pecb";
import { formatCohortRange } from "../lib/cohorts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending_stripe: { color: "bg-yellow-100 text-yellow-800", label: "Awaiting Card Payment" },
  pending_bank_transfer: { color: "bg-yellow-100 text-yellow-800", label: "Pending Bank Transfer" },
  proof_submitted: { color: "bg-blue-100 text-blue-800", label: "Proof Submitted — Verifying" },
  paid: { color: "bg-green-100 text-green-800", label: "Paid — Enrolled" },
  needs_clarification: { color: "bg-orange-100 text-orange-800", label: "Needs Clarification" },
  rejected: { color: "bg-red-100 text-red-800", label: "Rejected" },
  cancelled: { color: "bg-slate-200 text-slate-700", label: "Cancelled" },
};

const ENROLLMENT_STATUS = (status) => {
  if (status === "paid") return { color: "bg-green-100 text-green-800", label: "Active" };
  if (status === "cancelled" || status === "rejected") return { color: "bg-red-100 text-red-800", label: "Cancelled" };
  return { color: "bg-slate-100 text-slate-800", label: "Not Yet Active" };
};

const OrderPortal = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, { params: { token } });
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrder(); }, [orderId, token]);

  if (loading) return <div className="min-h-screen bg-slate-50 p-8"><div className="container mx-auto max-w-3xl animate-pulse"><div className="h-96 skeleton rounded-xl" /></div></div>;
  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-3xl">
          <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error || "Order not found"}</AlertDescription></Alert>
        </div>
      </div>
    );
  }

  const orderShort = `#${order.id.slice(0, 8).toUpperCase()}`;
  const paymentBadge = order.attendees_pending
    ? { color: "bg-green-100 text-green-800", label: "Paid" }
    : STATUS_BADGES[order.status] || { color: "bg-slate-100", label: order.status };
  const enrollment = order.attendees_pending
    ? { color: "bg-amber-100 text-amber-800", label: "Awaiting Attendee Names" }
    : ENROLLMENT_STATUS(order.status);
  const isBank = order.payment_method === "bank_transfer";
  const proof = order.proof_of_payment;
  // Course title is the only data we need to detect PECB requirement here
  const requiresPecb = courseRequiresPecb({ title: order.course_title, category: order.course_category });

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="container mx-auto px-4 lg:px-8 max-w-4xl space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-primary" data-testid="order-portal-title">My Order</h1>
            <p className="text-sm text-muted-foreground">Order {orderShort}</p>
          </div>
          <Button variant="outline" onClick={fetchOrder} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground">Payment Status</p>
              <Badge className={`${paymentBadge.color} mt-2`} data-testid="payment-status-badge">{paymentBadge.label}</Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground">Enrollment Status</p>
              <Badge className={`${enrollment.color} mt-2`} data-testid="enrollment-status-badge">{enrollment.label}</Badge>
            </CardContent>
          </Card>
        </div>

        {order.attendees_pending && (
          <Card className="border-brand/40 bg-brand/5" data-testid="attendees-pending-card">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm">Payment received. Add your {order.participants} attendees to complete enrollment.</p>
              <Link to={`/orders/${order.id}/attendees?token=${token}`}>
                <Button className="bg-brand text-white" data-testid="add-attendees-btn">Add Attendees</Button>
              </Link>
            </CardContent>
          </Card>
        )}


        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-brand" /> Order Details</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <Detail label="Course" value={order.course_title} />
            <Detail label="Format" value={order.delivery_format?.replace(/-/g, " ")} />
            {order.cohort && <Detail raw label="Course Dates" value={`${formatCohortRange(order.cohort)}${order.cohort.trainer_name ? ` · Trainer: ${order.cohort.trainer_name}` : ""}`} />}
            <Detail label="Customer" value={order.customer_name} />
            <Detail label="Email" value={order.customer_email} raw />
            <Detail label="Payment Method" value={order.payment_method === "stripe" ? "Credit/Debit Card" : "Bank Transfer"} />
            <Detail raw label="Amount" value={`USD ${(order.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
            <Detail label="Created" value={new Date(order.created_at).toLocaleString()} />
            {order.paid_at && <Detail label="Paid At" value={new Date(order.paid_at).toLocaleString()} />}
          </CardContent>
        </Card>

        {/* Bank Transfer actions */}
        {isBank && (order.status === "pending_bank_transfer" || order.status === "needs_clarification") && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Banknote className="h-6 w-6 text-yellow-700 mt-1" />
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg">{order.status === "needs_clarification" ? "Action Required" : "Awaiting Payment"}</h3>
                  <p className="text-sm text-yellow-900">
                    {order.status === "needs_clarification"
                      ? (order.admin_notes || "Please contact support or resubmit your proof of payment.")
                      : "Complete your bank transfer using the account details we sent to your email, then submit proof here."}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to={`/orders/${order.id}/bank-transfer?token=${token}`}>
                  <Button variant="outline" data-testid="view-bank-details-btn">View Bank Details</Button>
                </Link>
                <Link to={`/orders/${order.id}/payment-proof?token=${token}`}>
                  <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="submit-proof-btn"><Upload className="h-4 w-4 mr-2" /> Submit Proof of Payment</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Proof submitted info */}
        {proof && (
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">{proof.recorded_by_admin ? "Payment Received" : "Proof of Payment Submitted"}</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
              <Detail label="Submitted On" value={proof.submitted_at ? new Date(proof.submitted_at).toLocaleString() : "—"} />
              <Detail raw label="Amount Transferred" value={`USD ${Number(proof.amount_transferred).toLocaleString("en-US", { minimumFractionDigits: 2 })}`} />
              <Detail label="Bank Used" value={proof.bank_used} />
              <Detail label="Transaction Reference" value={proof.transaction_reference} />
              <Detail label="Date of Transfer" value={proof.date_of_transfer} />
              {!proof.recorded_by_admin && <Detail label="File" value={proof.original_filename || "—"} />}
            </CardContent>
          </Card>
        )}

        {/* Paid / Enrollment Active */}
        {order.status === "paid" && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-700 mt-1" />
                <div>
                  <h3 className="font-heading font-semibold text-lg text-green-900">{order.attendees_pending ? "Payment Received" : "Enrollment Active"}</h3>
                  <p className="text-sm text-green-800 mt-1">
                    {order.attendees_pending
                      ? "Thank you — your payment is confirmed. Add your attendees above so we can send each of them their course access details."
                      : `Welcome to ${order.course_title}! Course access instructions have been sent to ${order.customer_email}.`}
                  </p>
                  <Button asChild size="sm" variant="outline" className="mt-3 border-green-700 text-green-800">
                    <a href={`${API}/orders/${order.id}/receipt?token=${token}`} data-testid="portal-download-receipt">
                      <FileText className="h-4 w-4 mr-2" /> Download Receipt{order.receipt_number ? ` ${order.receipt_number}` : ""}
                    </a>
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">Need help? <a href="mailto:admin@comply-cms.com" className="text-brand hover:underline">Contact support</a></p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PECB notice */}
        {requiresPecb && (
          <Card className="border-blue-200 bg-blue-50" data-testid="order-pecb-notice">
            <CardContent className="p-5 flex items-start gap-3">
              <GraduationCap className="h-6 w-6 text-blue-700 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-blue-900">PECB Student Account Required</h3>
                <p className="text-sm text-blue-800 mt-1">
                  Completion of this course requires a free PECB Student Account. Use it to access course materials and your certification exam.
                </p>
                <a href={PECB_SIGNUP_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-brand hover:underline">
                  Create / sign in to your PECB account <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-muted-foreground">
          <Mail className="inline h-4 w-4 mr-1" /> Questions? <a href="mailto:admin@comply-cms.com" className="text-brand hover:underline">admin@comply-cms.com</a>
        </p>
      </div>
    </div>
  );
};

const Detail = ({ label, value, raw }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`font-medium ${raw ? "" : "capitalize"}`}>{value || "—"}</p>
  </div>
);

export default OrderPortal;
